import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';

import { RunStatus } from '../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { Run } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { getRunType } from '@/helpers/runs';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@/new/components/shadcn/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TableNullField } from '@/new/components/table/table-null-field';
import { TablePagination } from '@/new/components/table/table-pagination';

const getStatusDisplay = (run: Run) => {
  if (run.status === RunStatus.AwaitingApproval) {
    return {
      text: 'Awaiting Approval',
      icon: <Icon className="lucide--shield-alert h-4 w-4 text-amber-600" />,
    };
  }
  if (run.status === RunStatus.Failed) {
    return {
      text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Failed',
      icon: <Icon className="lucide--x-circle h-4 w-4 text-red-500" />,
    };
  }
  if (run.completedAt) {
    const status = run.status?.toLowerCase() || '';
    if (
      status.includes('fail') ||
      (run.failedPagesCount && run.failedPagesCount > 0 && run.totalPages === run.failedPagesCount)
    ) {
      return {
        text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Failed',
        icon: <Icon className="lucide--x-circle h-4 w-4 text-red-500" />,
      };
    }
    return {
      text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Completed',
      icon: <Icon className="lucide--check-circle h-4 w-4 text-green-500" />,
    };
  }

  if (run.inQueue) {
    const queuePosition = run.queuePosition !== null ? `, position ${run.queuePosition + 1}` : '';
    return {
      text: `In queue${queuePosition}`,
      icon: <Icon className="lucide--loader-circle h-4 w-4 animate-spin text-amber-500 duration-700" />,
    };
  }
  return {
    text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Processing',
    icon: <Icon className="lucide--loader-circle h-4 w-4 animate-spin text-amber-500 duration-700" />,
  };
};

const formatNumber = (num: number) => {
  return num.toLocaleString();
};

const columnHelper = createColumnHelper<Run>();

export function RunLog() {
  const { matterId } = useParams();
  if (!matterId) return null;
  const { data: runs, isLoading } = useGetMatterRuns(matterId);

  const columns = [
    columnHelper.accessor('createdDate', {
      header: () => <span>Date</span>,
      cell: (info) => (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Icon className="lucide--clock h-3 w-3" />
          <span>{formatDate(info.getValue(), true, true)}</span>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: () => <span>Status</span>,
      cell: (info) => {
        const statusDisplay = getStatusDisplay(info.row.original);
        return (
          <div className="flex items-center gap-2">
            {statusDisplay.icon}
            <span className="text-xs">{statusDisplay.text}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor('type', {
      header: () => <span>Type</span>,
      cell: (info) => (
        <Badge className="rounded-sm border-transparent bg-gray-50 text-gray-700 ring-1 ring-gray-200">
          {getRunType(info.row.original, !info.row.original.case)}
        </Badge>
      ),
    }),
    columnHelper.accessor('case', {
      header: () => <span>Subset</span>,
      cell: (info) => {
        const caseData = info.getValue();
        return caseData ? (
          <Tooltip>
            <TooltipTrigger>
              <p className="max-w-36 truncate text-xs">{caseData.name}</p>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">{caseData.name}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-xs text-gray-400">All Documents</span>
        );
      },
    }),
    columnHelper.accessor('createdBy.email', {
      header: () => <span>Submitted By</span>,
      cell: (info) => {
        const email = info.getValue();
        return email ? <span className="text-xs text-gray-900">{email}</span> : <TableNullField />;
      },
    }),
    columnHelper.accessor('createdDate', {
      header: () => <span>Date</span>,
      cell: (info) => (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Icon className="lucide--clock h-3 w-3" />
          <span>{formatDate(info.getValue(), true, true)}</span>
        </div>
      ),
    }),
    columnHelper.accessor('successfulPagesCount', {
      header: () => <span>Processed</span>,
      cell: (info) => {
        const count = info.getValue();
        return count !== undefined && count > 0 ? (
          <div className="flex items-center gap-1.5 text-xs">
            <span className="h-1.5 w-1.5 rounded-full bg-green-700" />
            <span className="font-medium text-green-700">{formatNumber(count)} pages</span>
          </div>
        ) : (
          <TableNullField />
        );
      },
    }),
    columnHelper.accessor('failedPagesCount', {
      header: () => <span>Failed</span>,
      cell: (info) => {
        const count = info.getValue();
        return count > 0 ? (
          <Button variant="destructive" size="sm" asChild className="group text-xs">
            <Link
              to={`/matters/${matterId}/settings/file-history/run-log/${info.row.original.id}`}
              onClick={() =>
                trackCustomEvent(TrackingEventsV2.FileHistoryClickFailedPages, {
                  matterId,
                  runId: info.row.original.id,
                })
              }
            >
              <Icon className="lucide--alert-triangle mr-1 h-3 w-3" />
              {formatNumber(count)} pages
              <Icon className="lucide--arrow-right ml-1 h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.25" />
            </Link>
          </Button>
        ) : (
          <TableNullField />
        );
      },
    }),
  ];

  const table = useReactTable({
    data: runs ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!runs || runs.length === 0) {
    return <div className="py-12 text-center text-sm text-gray-500">No processing runs to display</div>;
  }

  return (
    <>
      <div className="my-2 flex items-center gap-1.5">
        <Icon className="lucide--info size-3 text-gray-500" />
        <p className="text-xs text-gray-600">
          Runs happen when Wexler processes your documents (e.g. extracts facts from new documents, or assesses
          relevance of facts when creating a subset).
        </p>
      </div>
      <TableComponent>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </TableComponent>
      {runs.length > 0 && <TablePagination table={table} plural="runs" />}
    </>
  );
}
